
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { synczbase } from '../lib/synczbase';
import { AppNotification } from '../types';
import { Toast } from '../components/ui/Toast';

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  markAsRead: async () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [toasts, setToasts] = useState<AppNotification[]>([]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }

    let isMounted = true;
    const uid = user.uid || user.id;

    const fetchNotifications = async () => {
        try {
            const res = await synczbase.query(
                "SELECT * FROM notifications WHERE \"userId\" = $1 ORDER BY \"createdAt\" DESC LIMIT 50", 
                [uid]
            );
            if (res && isMounted) {
                const msgs = res.map((row: any) => {
                    let parsed: any = {};
                    if (row.data) {
                        try {
                            parsed = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
                        } catch (e) {}
                    }
                    return {
                        id: row.id,
                        userId: row.userId || parsed.userId,
                        title: row.title || parsed.title,
                        message: row.message || parsed.message,
                        type: parsed.type || row.type || 'INFO',
                        read: row.read === true || row.read === 'true' || parsed.read === true,
                        createdAt: Number(row.createdAt || parsed.createdAt || Date.now()),
                        link: row.link || parsed.link
                    } as AppNotification;
                });
                setNotifications(prev => {
                    msgs.forEach(newMsg => {
                        if (newMsg.createdAt > (Date.now() - 10000) && !newMsg.read) {
                            const existing = prev.find(p => p.id === newMsg.id);
                            if (!existing) {
                                setToasts(tPrev => [...tPrev, newMsg]);
                            }
                        }
                    });
                    return msgs;
                });
            }
        } catch(e) { console.error("Notifs fetch error", e); }
    };

    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 10000);

    return () => {
        isMounted = false;
        clearInterval(intervalId);
    };
  }, [user]);

  const markAsRead = async (id: string) => {
    try {
      const notif = notifications.find(n => n.id === id);
      if (notif) {
        const updated = { ...notif, read: true };
        await synczbase.query("UPDATE notifications SET read = true, data = $2 WHERE id = $1", [id, JSON.stringify(updated)]);
      } else {
        await synczbase.query("UPDATE notifications SET read = true WHERE id = $1", [id]);
      }
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (e) {
      console.error("Failed to mark read", e);
    }
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    markAsRead(id);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAsRead }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3">
        {toasts.map(toast => (
          <Toast key={toast.id} notification={toast} onClose={removeToast} />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
